import { Fragment, useState, useEffect } from 'react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import PlanoContasSelect from '../components/PlanoContasSelect'
import ContaSelect from '../components/ContaSelect'
import FinalidadeCompraSelect from '../components/FinalidadeCompraSelect'
import CentroCustoSelect from '../components/CentroCustoSelect'
import ItemSelect from '../components/ItemSelect'
import LookupSelect from '../components/LookupSelect'

interface Parcom {
  pa5numsol: number
  pa5infsol: string
  pa5exilib: string
  pa5infcon: string
  pa5comdti: string
  pa5comdtf: string
}

function fmtQtd(v: string) { return (Number(String(v).replace(',', '.')) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3 }) }
function fmtData(iso: string) { return iso ? iso.slice(0, 10).split('-').reverse().join('/') : '-' }

const thItens: React.CSSProperties = {
  padding: '9px 10px', textAlign: 'center', color: '#fff', fontWeight: 700,
  fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap',
}
const tdItens: React.CSSProperties = { padding: '8px 10px', color: '#374151' }

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: '#f7faff', border: '1px solid #eef0f4', borderLeft: '4px solid #1a6ea8',
      borderRadius: '0.5rem', padding: '0.7rem 0.9rem', marginBottom: '0.7rem',
    }}>
      <h4 style={{ fontSize: '0.68rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
        {titulo}
      </h4>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.8rem', alignItems: 'flex-end' }}>
        {children}
      </div>
    </div>
  )
}

function Campo({ label, width, children }: { label: string; width?: number | string; children: React.ReactNode }) {
  return (
    <div className="field-cad" style={{ width, gap: '0.15rem' }}>
      <label style={{ fontSize: '0.66rem' }}>{label}</label>
      {children}
    </div>
  )
}

function CampoBusca({ label, width, children }: { label: string; width?: number | string; children: React.ReactNode }) {
  return (
    <div style={{ width }}>
      <label style={{ display: 'block', fontSize: '0.66rem', fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.2rem' }}>
        {label}
      </label>
      {children}
    </div>
  )
}

const SITUACAO_LABELS: Record<string, { texto: string; cor: string; fundo: string }> = {
  '00': { texto: 'Pendente', cor: '#991b1b', fundo: '#fee2e2' },
  'XX': { texto: 'Aguardando', cor: '#92400e', fundo: '#fef3c7' },
  CA: { texto: 'Cancelada', cor: '#6b7280', fundo: '#f3f4f6' },
}

interface ItemSolicitacao {
  linha: number
  itemCod: string
  itemDes: string
  referenciaCod: string
  quantidade: string
  unidade: string
  dataEntrega: string
  destCompraCod: string
  destCompraDes: string
  statusLabel?: string
  motivoCancelamento?: string
}

export default function SolicitacaoCompra() {
  const { empresas, empresaCod, setEmpresaCod, empresaAtual } = useEmpresa()
  const [etapa, setEtapa] = useState<'capa' | 'itens'>('capa')

  function hojeISO() { return new Date().toISOString().slice(0, 10) }
  function agoraTexto() {
    const agora = new Date()
    return `${String(agora.getHours()).padStart(2, '0')},${String(agora.getMinutes()).padStart(2, '0')}`
  }

  const [nomeSolicitante, setNomeSolicitante] = useState('')
  const [dataSolicitacao, setDataSolicitacao] = useState(hojeISO)
  const [horaSolicitacao, setHoraSolicitacao] = useState(agoraTexto)

  const [finalidadeCod, setFinalidadeCod] = useState('')
  const [, setFinalidadeDes] = useState('')
  const [centroCustoCod, setCentroCustoCod] = useState('')
  const [, setCentroCustoDes] = useState('')
  const [contaFinanceiraCod, setContaFinanceiraCod] = useState('')
  const [, setContaFinanceiraDes] = useState('')
  const [ctaAdministrativaCod, setCtaAdministrativaCod] = useState('')
  const [, setCtaAdministrativaDes] = useState('')

  const [observacoes, setObservacoes] = useState('')

  const [situacaoCod, setSituacaoCod] = useState('00')
  const [motivoCancelamento, setMotivoCancelamento] = useState('')
  const [dtLib] = useState('')
  const [urgente, setUrgente] = useState('N')
  const [concluida, setConcluida] = useState('N')

  const [parcom, setParcom] = useState<Parcom | null>(null)
  useEffect(() => {
    if (!empresaCod) { setParcom(null); return }
    api.get(`/erp/parcom/${empresaCod}`).then(r => setParcom(r.data)).catch(() => setParcom(null))
  }, [empresaCod])
  const proximoNumero = parcom ? parcom.pa5numsol + 1 : null

  const [numeroInput, setNumeroInput] = useState('')
  const [modoEdicao, setModoEdicao] = useState(false)
  const numeroExcedeLimite = proximoNumero !== null && numeroInput !== '' && Number(numeroInput) > proximoNumero
  useEffect(() => {
    if (!numeroInput && proximoNumero) setNumeroInput(String(proximoNumero))
  }, [proximoNumero])

  function limparFormularioCompleto() {
    setNomeSolicitante(''); setDataSolicitacao(hojeISO()); setHoraSolicitacao(agoraTexto())
    setFinalidadeCod(''); setCentroCustoCod(''); setContaFinanceiraCod(''); setCtaAdministrativaCod('')
    setObservacoes(''); setSituacaoCod('00'); setMotivoCancelamento(''); setUrgente('N')
    setItensSolicitacao([])
  }

  useEffect(() => {
    if (!empresaCod || !numeroInput || !/^\d+$/.test(numeroInput)) return
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/erp/solicitacao-compra/${empresaCod}/${numeroInput}`)
        const d = r.data
        setNomeSolicitante(d.nomeSolicitante); setDataSolicitacao(d.dataSolicitacao || hojeISO())
        setHoraSolicitacao(d.horaSolicitacao || agoraTexto())
        setFinalidadeCod(d.finalidadeCod); setCentroCustoCod(d.centroCustoCod)
        setContaFinanceiraCod(d.contaFinanceiraCod); setCtaAdministrativaCod(d.ctaAdministrativaCod)
        setObservacoes(d.observacoes); setSituacaoCod(d.situacao || '00')
        setMotivoCancelamento(d.motivoCancelamento || ''); setUrgente(d.urgente || 'N')
        setItensSolicitacao(d.itens || [])
        const primeiro = (d.itens || [])[0]
        if (primeiro) {
          setItemCod(primeiro.itemCod); setItemDes(primeiro.itemDes); setReferenciaCod(primeiro.referenciaCod)
          setQuantidadeItem(primeiro.quantidade); setUnidadeItem(primeiro.unidade); setDataEntregaItem(primeiro.dataEntrega)
          setDestCompraCod(primeiro.destCompraCod); setDestCompraDes(primeiro.destCompraDes)
          setLinhaAtual(1)
        } else {
          limparFormularioItem(1)
        }
        setModoEdicao(true)
      } catch {
        if (modoEdicao) { limparFormularioCompleto(); setModoEdicao(false) }
      }
    }, 500)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaCod, numeroInput])

  const situacao = SITUACAO_LABELS[situacaoCod]

  const [itensSolicitacao, setItensSolicitacao] = useState<ItemSolicitacao[]>([])
  const [itemCod, setItemCod] = useState('')
  const [itemDes, setItemDes] = useState('')
  const [referenciaCod, setReferenciaCod] = useState('')
  const [quantidadeItem, setQuantidadeItem] = useState('')
  const [unidadeItem, setUnidadeItem] = useState('')
  const [dataEntregaItem, setDataEntregaItem] = useState(dataSolicitacao)
  const [destCompraCod, setDestCompraCod] = useState('')
  const [destCompraDes, setDestCompraDes] = useState('')

  const [saldoQtd, setSaldoQtd] = useState<number | null>(null)
  const [ultimaCompraData, setUltimaCompraData] = useState('')
  const [ultimaCompraValor, setUltimaCompraValor] = useState<number | null>(null)
  useEffect(() => {
    if (!itemCod) { setSaldoQtd(null); setUltimaCompraData(''); setUltimaCompraValor(null); return }
    api.get(`/erp/itens/${itemCod}/saldo`).then(r => setSaldoQtd(r.data.saldoQtd)).catch(() => setSaldoQtd(null))
    api.get(`/erp/itens/${itemCod}/ultima-compra`).then(r => {
      setUltimaCompraData(r.data.data ? String(r.data.data).slice(0, 10) : '')
      setUltimaCompraValor(r.data.valorUnitario)
    }).catch(() => { setUltimaCompraData(''); setUltimaCompraValor(null) })
  }, [itemCod])

  const [linhaAtual, setLinhaAtual] = useState(1)

  function limparFormularioItem(linha: number) {
    setItemCod(''); setItemDes(''); setReferenciaCod(''); setQuantidadeItem(''); setUnidadeItem('')
    setDataEntregaItem(dataSolicitacao); setDestCompraCod(''); setDestCompraDes(''); setLinhaAtual(linha)
  }

  function selecionarLinha(numero: number) {
    const max = itensSolicitacao.length + 1
    const alvo = Math.min(Math.max(1, numero), max)
    const existente = itensSolicitacao.find(it => it.linha === alvo)
    if (existente) {
      setItemCod(existente.itemCod); setItemDes(existente.itemDes); setReferenciaCod(existente.referenciaCod)
      setQuantidadeItem(existente.quantidade); setUnidadeItem(existente.unidade); setDataEntregaItem(existente.dataEntrega)
      setDestCompraCod(existente.destCompraCod); setDestCompraDes(existente.destCompraDes); setLinhaAtual(alvo)
    } else {
      limparFormularioItem(alvo)
    }
  }

  function adicionarItem() {
    const item: ItemSolicitacao = {
      linha: linhaAtual, itemCod, itemDes, referenciaCod,
      quantidade: quantidadeItem, unidade: unidadeItem, dataEntrega: dataEntregaItem,
      destCompraCod, destCompraDes,
    }
    const jaExistia = itensSolicitacao.some(it => it.linha === linhaAtual)
    setItensSolicitacao(prev => jaExistia ? prev.map(it => it.linha === linhaAtual ? item : it) : [...prev, item])
    const novoTotalLinhas = jaExistia ? itensSolicitacao.length : itensSolicitacao.length + 1
    limparFormularioItem(novoTotalLinhas + 1)
  }

  function removerItem(linha: number) {
    setItensSolicitacao(prev => prev.filter(it => it.linha !== linha).map((it, i) => ({ ...it, linha: i + 1 })))
    limparFormularioItem(itensSolicitacao.length)
  }

  const quantTotal = itensSolicitacao.reduce((s, it) => s + (Number(it.quantidade) || 0), 0)

  const [salvando, setSalvando] = useState(false)
  const [salvo, setSalvo] = useState<{ numero: number } | null>(null)
  const [erroSalvar, setErroSalvar] = useState('')

  async function salvarSolicitacao() {
    setSalvando(true); setErroSalvar(''); setSalvo(null)
    try {
      const r = await api.post('/erp/solicitacao-compra', {
        empcod: empresaCod, numero: Number(numeroInput), nomeSolicitante, dataSolicitacao, horaSolicitacao,
        finalidadeCod, centroCustoCod, contaFinanceiraCod, ctaAdministrativaCod,
        observacoes, urgente, itens: itensSolicitacao,
      })
      setSalvo({ numero: r.data.numero })
    } catch (e: any) {
      setErroSalvar(e?.response?.data?.error || 'Não foi possível salvar a solicitação. Tente novamente.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div style={{ maxWidth: 720 }}>
      {etapa === 'capa' && (
      <div className="card" style={{ padding: '1.1rem 1.3rem' }}>
        <h3 style={{
          fontSize: '0.98rem', fontWeight: 700, marginBottom: '0.8rem', color: '#1a6ea8',
          paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff',
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        }}>
          Solicitação de Compra
          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#111827' }}>TSOLCOM</span>
        </h3>

        <Secao titulo="Cabeçalho">
          <Campo label="Estab." width={55}>
            <select value={empresaCod ?? ''} onChange={e => setEmpresaCod(Number(e.target.value))}>
              {empresas.map(e => <option key={e.empcod} value={e.empcod}>{e.empcod}</option>)}
            </select>
          </Campo>
          <Campo label="Nome do Estabelecimento" width={190}>
            <input type="text" value={empresaAtual?.empfan || empresaAtual?.empnom || ''} readOnly disabled />
          </Campo>
          <Campo label="Número" width={90}>
            <input type="text" inputMode="numeric" value={numeroInput}
              onChange={e => setNumeroInput(e.target.value.replace(/\D/g, ''))}
              style={{ fontWeight: 700, color: numeroExcedeLimite ? '#dc2626' : '#1a6ea8', borderColor: numeroExcedeLimite ? '#dc2626' : undefined }} />
          </Campo>
          <Campo label="Data" width={140}>
            <input type="date" value={dataSolicitacao} readOnly disabled />
          </Campo>
          <Campo label="Hora" width={80}>
            <input type="text" value={horaSolicitacao} readOnly disabled />
          </Campo>
          <Campo label="Nome Solicitante" width={130}>
            <input type="text" maxLength={10} value={nomeSolicitante} onChange={e => setNomeSolicitante(e.target.value)} />
          </Campo>
          {modoEdicao ? (
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#92400e', background: '#fef3c7', padding: '0.3rem 0.6rem', borderRadius: 6 }}>
              Editando solicitação nº {numeroInput} já existente
            </span>
          ) : (
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
              Nova solicitação — número não pode passar de {proximoNumero ?? '...'}
            </span>
          )}
        </Secao>

        <Secao titulo="Classificação">
          <CampoBusca label="Finalidade" width={260}>
            <FinalidadeCompraSelect value={finalidadeCod} onChange={(cod, des) => { setFinalidadeCod(cod); setFinalidadeDes(des) }} />
          </CampoBusca>
          <CampoBusca label="Centro de Custo" width={260}>
            <CentroCustoSelect value={centroCustoCod} onChange={(cod, des) => { setCentroCustoCod(cod); setCentroCustoDes(des) }} />
          </CampoBusca>
        </Secao>

        <Secao titulo="Contas">
          <CampoBusca label="Conta Financeira" width={260}>
            <ContaSelect value={contaFinanceiraCod} onChange={(cod, des) => { setContaFinanceiraCod(cod); setContaFinanceiraDes(des) }} />
          </CampoBusca>
          <CampoBusca label="Cta Administrativa" width={260}>
            <PlanoContasSelect empcod={empresaCod} value={ctaAdministrativaCod}
              onChange={(cod, des) => { setCtaAdministrativaCod(cod); setCtaAdministrativaDes(des) }} />
          </CampoBusca>
        </Secao>

        <Secao titulo="Observações">
          <textarea rows={4} maxLength={1200} value={observacoes} onChange={e => setObservacoes(e.target.value)}
            style={{
              width: '100%', padding: '0.5rem 0.7rem', border: '1.5px solid #d1d5db', borderRadius: '0.4rem',
              fontSize: '0.85rem', fontFamily: 'inherit', resize: 'none', background: '#fff',
            }} />
        </Secao>

        <Secao titulo="Situação e Liberação">
          <Campo label="Situação" width={60}>
            <input type="text" value={situacaoCod} readOnly disabled />
          </Campo>
          {situacao && (
            <span style={{
              fontSize: '0.75rem', fontWeight: 700, padding: '0.4rem 0.7rem', borderRadius: 6,
              background: situacao.fundo, color: situacao.cor, marginBottom: 2,
            }}>
              {situacao.texto}
            </span>
          )}
          <Campo label="Dt. Liberação" width={140}>
            <input type="date" value={dtLib} readOnly disabled />
          </Campo>
          {situacaoCod === 'CA' && motivoCancelamento && (
            <span style={{ fontSize: '0.78rem', color: '#dc2626', marginBottom: 2 }}>
              <b>Motivo do Cancelamento:</b> {motivoCancelamento}
            </span>
          )}
          <Campo label="Urgente?" width={95}>
            <select value={urgente} onChange={e => setUrgente(e.target.value)}>
              <option value="N">Não</option>
              <option value="S">Sim</option>
            </select>
          </Campo>
          <Campo label="Concluída?" width={95}>
            <select value={concluida} onChange={e => setConcluida(e.target.value)}>
              <option value="N">Não</option>
              <option value="S">Sim</option>
            </select>
          </Campo>
        </Secao>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: '0.9rem' }}>
          <button className="btn btn-primary" onClick={() => setEtapa('itens')} disabled={numeroExcedeLimite}>
            Próximo — Itens →
          </button>
          {(salvo || modoEdicao) && (
            <button className="btn btn-secondary"
              onClick={() => window.open(`/api/erp/solicitacao-compra/${empresaCod}/${salvo?.numero ?? numeroInput}/comprovante`, '_blank')}>
              Imprimir Comprovante
            </button>
          )}
          {numeroExcedeLimite && (
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#dc2626' }}>
              Número inválido — o próximo disponível é {proximoNumero}. Corrija antes de continuar.
            </span>
          )}
        </div>
      </div>
      )}

      {etapa === 'itens' && (
      <div className="card" style={{ padding: '1.1rem 1.3rem' }}>
        <h3 style={{
          fontSize: '0.98rem', fontWeight: 700, marginBottom: '0.8rem', color: '#1a6ea8',
          paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff',
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        }}>
          Solicitação de Compra — Itens
          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#111827' }}>TSOLITE</span>
        </h3>

        <Secao titulo="Novo Item">
          <Campo label="Linha" width={55}>
            <input type="number" min={1} value={linhaAtual}
              onChange={e => selecionarLinha(Number(e.target.value) || 1)}
              style={{ fontWeight: 700, color: '#1a6ea8' }} />
          </Campo>
          <CampoBusca label="Item" width={460}>
            <ItemSelect
              codValue={itemCod} descValue={itemDes} unValue={unidadeItem}
              onChange={(cod, des, un) => { setItemCod(cod); setItemDes(des); setUnidadeItem(un) }}
            />
          </CampoBusca>
        </Secao>

        <Secao titulo="Referência e Estoque">
          <Campo label="Referência" width={140}>
            <input type="text" value={referenciaCod} onChange={e => setReferenciaCod(e.target.value)} />
          </Campo>
          <Campo label="Estoque" width={100}>
            <input type="text" value={saldoQtd !== null ? saldoQtd.toLocaleString('pt-BR', { minimumFractionDigits: 3 }) : '0,000'} readOnly disabled />
          </Campo>
        </Secao>

        <Secao titulo="Quantidade e Valor">
          <Campo label="Quantidade" width={100}>
            <input type="number" min={0} value={quantidadeItem} onChange={e => setQuantidadeItem(e.target.value)} style={{ color: '#dc2626', fontWeight: 700 }} />
          </Campo>
          <Campo label="Unidade" width={70}>
            <input type="text" value={unidadeItem} readOnly disabled title="Vem do cadastro do item" />
          </Campo>
          <Campo label="Saldo" width={100}>
            <input type="text" value={(Number(quantidadeItem) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3 })} readOnly disabled />
          </Campo>
          <Campo label="Data Entrega" width={125}>
            <input type="date" value={dataEntregaItem} onChange={e => setDataEntregaItem(e.target.value)} />
          </Campo>
          <Campo label="Última Compra" width={140}>
            <input type="date" value={ultimaCompraData} readOnly disabled />
          </Campo>
          <Campo label="V. Unit." width={100}>
            <input type="text" value={ultimaCompraValor !== null ? ultimaCompraValor.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '0,00'} readOnly disabled />
          </Campo>
        </Secao>

        <Secao titulo="Destino">
          <CampoBusca label="Dest. Compra" width={90}>
            <LookupSelect
              endpoint="/erp/oedcad/lista?tipos=3,5"
              codKey="oed_cod"
              descKey="oed_des"
              renderItem={(item: any) => (
                <>
                  <div className="nat-item-linha1">
                    <span className="nat-item-cod">{item.oed_cod}</span>
                  </div>
                  <div className="nat-item-dsc">{item.oed_des}</div>
                </>
              )}
              codValue={destCompraCod}
              descValue={destCompraCod}
              onSelect={(cod, _des, item) => { setDestCompraCod(cod); setDestCompraDes(item?.oed_des ?? '') }}
              placeholder="Buscar..."
              listWidth={260}
            />
          </CampoBusca>
          <Campo label=" " width={220}>
            <input type="text" value={destCompraDes} readOnly disabled />
          </Campo>
        </Secao>

        <Secao titulo="Situação">
          <Campo label="Situação" width={110}>
            <select value="pendente" disabled>
              <option value="pendente">Pendente</option>
            </select>
          </Campo>
        </Secao>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1rem' }}>
          <button className="btn btn-secondary" onClick={adicionarItem} disabled={!itemCod || !quantidadeItem || !destCompraCod}>
            {itensSolicitacao.some(it => it.linha === linhaAtual) ? 'Salvar Linha ' + linhaAtual : '+ Adicionar Item'}
          </button>
          {itemCod && quantidadeItem && !destCompraCod && (
            <span style={{ fontSize: '0.78rem', color: '#dc2626' }}>Informe o Destino da Compra para adicionar o item.</span>
          )}
        </div>

        <div style={{ overflowX: 'auto', marginBottom: '1rem', border: '1px solid #e5e7eb', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: '#1a6ea8' }}>
                <th style={thItens}>Linha</th>
                <th style={{ ...thItens, textAlign: 'left' }}>Item</th>
                <th style={{ ...thItens, textAlign: 'right' }}>Quantidade</th>
                <th style={thItens}>Unidade</th>
                <th style={thItens}>Data Entrega</th>
                <th style={thItens}>Status</th>
                <th style={{ ...thItens, width: 32 }}></th>
              </tr>
            </thead>
            <tbody>
              {itensSolicitacao.length === 0 && (
                <tr><td colSpan={7} style={{ padding: '1.2rem', textAlign: 'center', color: '#9ca3af' }}>Nenhum item adicionado ainda.</td></tr>
              )}
              {itensSolicitacao.map((it, i) => (
                <Fragment key={it.linha}>
                  <tr style={{ background: i % 2 ? '#f7faff' : '#fff', borderBottom: it.motivoCancelamento ? 'none' : '1px solid #eef0f4' }}>
                    <td style={{ ...tdItens, textAlign: 'center', fontWeight: 700, color: '#1a6ea8' }}>{it.linha}</td>
                    <td style={tdItens}>
                      <span style={{ color: '#1a6ea8', fontWeight: 700 }}>{it.itemCod}</span> — {it.itemDes}
                    </td>
                    <td style={{ ...tdItens, textAlign: 'right', fontWeight: 700, color: '#92400e' }}>{fmtQtd(it.quantidade)}</td>
                    <td style={{ ...tdItens, textAlign: 'center' }}>{it.unidade}</td>
                    <td style={{ ...tdItens, textAlign: 'center', whiteSpace: 'nowrap' }}>{fmtData(it.dataEntrega)}</td>
                    <td style={{ ...tdItens, textAlign: 'center', color: it.statusLabel === 'Cancelada' ? '#dc2626' : undefined }}
                      title={it.motivoCancelamento || ''}>
                      {it.statusLabel || ''}
                    </td>
                    <td style={{ ...tdItens, textAlign: 'center' }}>
                      <button onClick={() => removerItem(it.linha)}
                        style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: 4, border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}>✕</button>
                    </td>
                  </tr>
                  {it.motivoCancelamento && (
                    <tr style={{ background: i % 2 ? '#f7faff' : '#fff', borderBottom: '1px solid #eef0f4' }}>
                      <td colSpan={7} style={{ padding: '0 10px 8px', color: '#dc2626', fontSize: '0.76rem' }}>
                        Motivo: {it.motivoCancelamento}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', marginBottom: '1rem' }}>
          <div style={{
            background: '#eff6ff', border: '1px solid #dbeafe', borderRadius: 8,
            padding: '0.5rem 0.9rem', display: 'flex', flexDirection: 'column', gap: 2,
          }}>
            <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Quantidade Total</span>
            <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#92400e' }}>{quantTotal.toLocaleString('pt-BR', { minimumFractionDigits: 3 })}</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => setEtapa('capa')}>
            ← Voltar
          </button>
          <button className="btn btn-primary" onClick={salvarSolicitacao} disabled={salvando || itensSolicitacao.length === 0 || !numeroInput}>
            {salvando ? 'Salvando...' : 'Salvar Solicitação'}
          </button>
          {salvo && (
            <>
              <button className="btn btn-secondary"
                onClick={() => window.open(`/api/erp/solicitacao-compra/${empresaCod}/${salvo.numero}/comprovante`, '_blank')}>
                Imprimir Comprovante
              </button>
              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#15803d' }}>
                ✓ Solicitação nº {salvo.numero} salva com sucesso!
              </span>
            </>
          )}
          {erroSalvar && (
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#dc2626' }}>{erroSalvar}</span>
          )}
        </div>
      </div>
      )}
    </div>
  )
}
